// R-131-FIN-DECISION-F — quick-edit overrides for pathway comparison picker.

import { KW_PER_MW } from '../projectFinance';
import { derivePathwayTimeline } from './pathwayCashflows';
import { addMonthsIso } from './presets/shared';
import { withSyncedRiskPremium } from './riskPremium';
import type { Pathway, PathwayPhase } from './types';

function utcYear(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00Z`).getUTCFullYear();
}

function shiftPhasesByYears(phases: PathwayPhase[], yearDelta: number): PathwayPhase[] {
  if (yearDelta === 0) return phases;
  const monthDelta = yearDelta * 12;
  return phases.map((phase) => ({
    ...phase,
    startDate: addMonthsIso(phase.startDate, monthDelta),
  }));
}

/**
 * Shift all phase anchors so COD lands in `targetCodYear`.
 *
 * C10 — an EXPLICIT `lifetime.decommissioningYear` is an absolute calendar year
 * derived from the original COD, so it has to travel with it. Left fixed, a
 * pathway pushed out five years paid to decommission a plant five years before
 * its design life ended — and, once the shift exceeded the gap, BEFORE it was
 * built. When the field is absent the year is derived from COD downstream and
 * needs no shift.
 */
export function applyCodYearOverride(pathway: Pathway, targetCodYear: number, now?: Date): Pathway {
  const anchor = now ?? new Date();
  const timeline = derivePathwayTimeline(pathway, anchor);
  const deltaYears = targetCodYear - timeline.codYear;
  return withSyncedRiskPremium({
    ...pathway,
    phases: shiftPhasesByYears(pathway.phases, deltaYears),
    capex: {
      ...pathway.capex,
      outflowsByYear: pathway.capex.outflowsByYear.map((row) => ({
        year: row.year + deltaYears,
        usd: row.usd,
      })),
    },
    lifetime: {
      ...pathway.lifetime,
      ...(pathway.lifetime.decommissioningYear != null
        ? { decommissioningYear: pathway.lifetime.decommissioningYear + deltaYears }
        : {}),
    },
  });
}

/** Scale a positive, finite absolute amount; leave anything else untouched. */
function scaled(value: number | undefined, ratio: number): number | undefined {
  return value != null && Number.isFinite(value) ? value * ratio : value;
}

/**
 * Scale capacity and re-derive capex from implied $/kW.
 *
 * C10 — this is the MAIN intent → pathway path (the quick-edit row on the
 * comparison picker), and it used to scale only `capex.baseUsd`, the phase
 * capex, the outflow schedule and the operation phase's energy. Every
 * ABSOLUTE-USD component stayed at its original size, so re-sizing a 100 MW
 * pathway to 300 MW kept the 100 MW substation, tie-line, fuel logistics,
 * working capital, decommissioning liability, residual value and refurbishment
 * events — and kept billing the 100 MW facility load's demand charge. The
 * absolute components are a larger share of a grid pathway than of a BTM one,
 * so the error also silently re-ordered the comparison.
 *
 * Everything that scales with plant size scales here. Rates (per-kW, per-MWh,
 * percentages) are deliberately NOT scaled — they are already intensive.
 */
export function applyCapacityOverride(pathway: Pathway, capacityMW: number): Pathway {
  if (capacityMW <= 0 || pathway.capacityMW <= 0) return pathway;
  const ratio = capacityMW / pathway.capacityMW;
  const capexPerKw = pathway.capex.baseUsd / (pathway.capacityMW * KW_PER_MW);
  const nextBaseCapex = capexPerKw * capacityMW * KW_PER_MW;

  const phases = pathway.phases.map((phase) => {
    if (phase.kind !== 'operation') {
      return phase.capexUsd != null
        ? { ...phase, capexUsd: phase.capexUsd * ratio }
        : phase;
    }
    return {
      ...phase,
      annualMwh: phase.annualMwh != null ? phase.annualMwh * ratio : phase.annualMwh,
      annualOpexUsd: phase.annualOpexUsd != null ? phase.annualOpexUsd * ratio : phase.annualOpexUsd,
      // A source mix carries per-component CAPACITY, so it scales with the
      // pathway. Leaving it fixed would keep the blend's energy and economics
      // at the original size while the pathway nameplate moved.
      sourceMix: phase.sourceMix?.map((c) => ({ ...c, fractionMw: c.fractionMw * ratio })),
    };
  });

  return withSyncedRiskPremium({
    ...pathway,
    capacityMW,
    // The metered load a demand charge is billed on scales with the plant it
    // serves; leaving it fixed under-bills every up-sized grid pathway.
    facilityLoadMW: scaled(pathway.facilityLoadMW, ratio),
    phases,
    capex: {
      ...pathway.capex,
      baseUsd: nextBaseCapex,
      substationUsd: scaled(pathway.capex.substationUsd, ratio),
      transmissionUpgradeUsd: scaled(pathway.capex.transmissionUpgradeUsd, ratio),
      fuelLogisticsUsd: scaled(pathway.capex.fuelLogisticsUsd, ratio),
      workingCapitalUsd: scaled(pathway.capex.workingCapitalUsd, ratio),
      outflowsByYear: pathway.capex.outflowsByYear.map((row) => ({
        ...row,
        usd: row.usd * ratio,
      })),
    },
    opex: {
      ...pathway.opex,
      refurbishmentEvents: pathway.opex.refurbishmentEvents?.map((e) => ({
        ...e,
        usd: e.usd * ratio,
      })),
    },
    lifetime: {
      ...pathway.lifetime,
      decommissioningUsd: pathway.lifetime.decommissioningUsd * ratio,
      residualValueUsd: scaled(pathway.lifetime.residualValueUsd, ratio),
    },
  });
}

export function applyLifetimeOverride(pathway: Pathway, designLifeYears: number): Pathway {
  if (designLifeYears <= 0) return pathway;
  const operationIdx = pathway.phases.findIndex((p) => p.kind === 'operation');
  if (operationIdx < 0) {
    return withSyncedRiskPremium({
      ...pathway,
      lifetime: { ...pathway.lifetime, designLifeYears },
    });
  }

  const phases = [...pathway.phases];
  const operation = phases[operationIdx];
  phases[operationIdx] = {
    ...operation,
    durationMonths: designLifeYears * 12,
  };

  return withSyncedRiskPremium({
    ...pathway,
    phases,
    lifetime: { ...pathway.lifetime, designLifeYears },
    revenue: {
      ...pathway.revenue,
      ppaTermYears: Math.max(pathway.revenue.ppaTermYears, designLifeYears),
    },
  });
}

export function applyCapexOverride(pathway: Pathway, baseUsd: number): Pathway {
  if (baseUsd <= 0) return pathway;
  const ratio = baseUsd / pathway.capex.baseUsd;
  const phases = pathway.phases.map((phase) =>
    phase.capexUsd != null ? { ...phase, capexUsd: phase.capexUsd * ratio } : phase,
  );
  return withSyncedRiskPremium({
    ...pathway,
    phases,
    capex: {
      ...pathway.capex,
      baseUsd,
      outflowsByYear: pathway.capex.outflowsByYear.map((row) => ({
        ...row,
        usd: row.usd * ratio,
      })),
    },
  });
}

export function applyWaccOverride(pathway: Pathway, baseWaccPct: number): Pathway {
  return withSyncedRiskPremium({
    ...pathway,
    risk: { ...pathway.risk, baseWaccPct },
  });
}

/** Resolve COD calendar year for quick-edit display. */
export function pathwayCodYear(pathway: Pathway, now?: Date): number {
  const anchor = now ?? new Date();
  return derivePathwayTimeline(pathway, anchor).codYear;
}

/** Resolve construction start year for timeline display. */
export function pathwayConstructionStartYear(pathway: Pathway, now?: Date): number {
  const anchor = now ?? new Date();
  return derivePathwayTimeline(pathway, anchor).constructionStartYear;
}

/** Years from analysis anchor to first MWh (COD). */
export function pathwayTimeToPowerYears(pathway: Pathway, now?: Date): number {
  const anchor = now ?? new Date();
  const timeline = derivePathwayTimeline(pathway, anchor);
  return Math.max(0, timeline.codYear - timeline.nowYear);
}

/** First operation phase start ISO — first MWh date proxy. */
export function pathwayFirstMwhDate(pathway: Pathway): string | null {
  const operation = pathway.phases.find((p) => p.kind === 'operation');
  return operation?.startDate ?? null;
}

/** Derive implied capex $/kW for display. */
export function pathwayCapexPerKw(pathway: Pathway): number {
  if (pathway.capacityMW <= 0) return 0;
  return pathway.capex.baseUsd / (pathway.capacityMW * KW_PER_MW);
}

/** Shift pathway name suffix when COD year changes via preset pick. */
export function pathwayDisplayNameWithCod(pathway: Pathway, now?: Date): string {
  const cod = pathwayCodYear(pathway, now);
  const base = pathway.name.replace(/\s+\d{4}$/, '');
  return `${base} ${cod}`;
}

/** Parse a yyyy-mm-dd or year-only COD target. */
export function parseCodYearInput(value: string | number): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  const trimmed = String(value).trim();
  if (/^\d{4}$/.test(trimmed)) return Number(trimmed);
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return utcYear(trimmed);
  return null;
}
